/**
 * Scrollytelling map — Meninas Mães (AzMina)
 *
 * Custom implementation in Mapbox GL JS + Scrollama, following the
 * pattern described in https://www.mapbox.com/blog/how-to-build-a-scrollytelling-map
 * (the mapbox/storytelling "vanilla-js" template): a single Mapbox
 * Studio style, with named layers toggled on/off via opacity
 * (onChapterEnter/onChapterExit).
 *
 * No dependency on the JEO plugin.
 */

import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import scrollama from 'scrollama';
import config from './config';
import './story.css';

// Mapbox GL has no generic "opacity" paint property: the name changes
// depending on the layer type. This replicates the helper from the
// official mapbox/storytelling template.
function getLayerOpacityProps(layerType) {
  switch (layerType) {
    case 'fill':
      return ['fill-opacity'];
    case 'line':
      return ['line-opacity'];
    case 'circle':
      return ['circle-opacity'];
    case 'symbol':
      return ['icon-opacity', 'text-opacity'];
    case 'raster':
      return ['raster-opacity'];
    case 'fill-extrusion':
      return ['fill-extrusion-opacity'];
    case 'heatmap':
      return ['heatmap-opacity'];
    default:
      return [];
  }
}

function setLayerOpacity(map, layerId, opacity) {
  const layer = map.getLayer(layerId);

  if (!layer) {
    // eslint-disable-next-line no-console
    console.warn(`[meninas-maes-storymap] layer "${layerId}" not found in the style.`);
    return;
  }

  getLayerOpacityProps(layer.type).forEach((prop) => {
    map.setPaintProperty(layerId, prop, opacity);
  });
}

// The layer order in the style (set in Mapbox Studio) interleaves text
// labels (type: 'symbol') with the chapters' polygons — that's why some
// place names (e.g. "Campinápolis") end up below the polygons, while
// others (e.g. "Rio de Janeiro") end up on top. `map.moveLayer(id)` with
// no second argument moves the layer to the top of the stack; calling
// this in order for every existing 'symbol' layer makes all labels
// render above any polygon/line, while keeping their relative order.
function moveLabelsToTop(map) {
  map.getStyle().layers.forEach((layer) => {
    if (layer.type === 'symbol') {
      map.moveLayer(layer.id);
    }
  });
}

// Every layer referenced by any chapter at some point (onChapterEnter or
// onChapterExit). Used to zero out the whole set before turning on only
// what the current chapter asks for — see the comment on enterChapter,
// below.
function collectAllLayerIds(chapters) {
  const layerIds = new Set();

  chapters.forEach((chapter) => {
    (chapter.onChapterEnter || []).forEach(({ layer }) => layerIds.add(layer));
    (chapter.onChapterExit || []).forEach(({ layer }) => layerIds.add(layer));
  });

  return layerIds;
}

function init(rootEl) {
  const mapEl = rootEl.querySelector('.mm-story__map');
  const featuresEl = rootEl.querySelector('.mm-story__features');

  // The accessToken doesn't come from the versioned config.js — it's
  // injected at runtime by PHP via wp_localize_script (see
  // inc/shortcodes/meninas-maes-storymap.php).
  const accessToken = window.azMeninasMaesStorymap && window.azMeninasMaesStorymap.accessToken;

  if (!accessToken) {
    // eslint-disable-next-line no-console
    console.error('[meninas-maes-storymap] Mapbox accessToken not configured. Set MAPBOX_ACCESS_TOKEN in .env.');
    return;
  }

  mapboxgl.accessToken = accessToken;

  rootEl.classList.add(`mm-story--${config.theme || 'light'}`);
  rootEl.classList.add(`mm-story--align-${config.alignment || 'left'}`);

  const firstChapter = config.chapters[0];

  const map = new mapboxgl.Map({
    container: mapEl,
    style: config.style,
    center: firstChapter.location.center,
    zoom: firstChapter.location.zoom,
    pitch: firstChapter.location.pitch || 0,
    bearing: firstChapter.location.bearing || 0,
    interactive: false, // navigation happens via scroll, as in the original template
    attributionControl: true,
    projection: 'mercator',
  });

  // Builds the HTML for each chapter (text), with the structure that
  // Scrollama observes to trigger view/layer changes.
  config.chapters.forEach((chapter, index) => {
    const section = document.createElement('div');
    section.className = 'mm-story__chapter';
    section.id = chapter.id;
    section.setAttribute('data-chapter-index', String(index));

    const box = document.createElement('div');
    box.className = 'mm-story__chapter-box';

    // title and description come from config.js (not user input), so
    // raw HTML is accepted on purpose — allows <strong>, <a> etc.
    if (chapter.title) {
      const h3 = document.createElement('h3');
      h3.innerHTML = chapter.title;
      box.appendChild(h3);
    }

    if (chapter.description) {
      const p = document.createElement('p');
      p.innerHTML = chapter.description;
      box.appendChild(p);
    }

    if (chapter.image) {
      const img = document.createElement('img');
      img.className = 'mm-story__chapter-image';
      img.src = chapter.image;
      img.alt = '';
      img.loading = 'lazy';
      box.appendChild(img);
    }

    section.appendChild(box);
    featuresEl.appendChild(section);
  });

  const allLayerIds = collectAllLayerIds(config.chapters);

  function applyLayerChanges(changes) {
    (changes || []).forEach(({ layer, opacity }) => {
      setLayerOpacity(map, layer, opacity);
    });
  }

  function hideAllLayers() {
    allLayerIds.forEach((layerId) => setLayerOpacity(map, layerId, 0));
  }

  // Chapters in the same group (e.g. 9-12, all turning on the same
  // "capitals" layers) leave onChapterExit empty so the layer doesn't
  // flicker when moving from one to the next. That's why every chapter
  // entry zeroes out ALL known layers before turning on only the ones
  // the current chapter asks for — the state never depends on
  // onChapterExit or on scroll direction/history.
  function enterChapter(chapter) {
    hideAllLayers();
    applyLayerChanges(chapter.onChapterEnter);
  }

  function setupScroll() {
    const scroller = scrollama();

    scroller
      .setup({
        step: rootEl.querySelectorAll('.mm-story__chapter'),
        offset: 0.6,
        progress: false,
      })
      .onStepEnter((response) => {
        const index = Number(response.element.getAttribute('data-chapter-index'));
        const chapter = config.chapters[index];

        rootEl.querySelectorAll('.mm-story__chapter').forEach((el) => {
          el.classList.remove('is-active');
        });
        response.element.classList.add('is-active');

        if (!chapter) {
          return;
        }

        map.flyTo({
          center: chapter.location.center,
          zoom: chapter.location.zoom,
          pitch: chapter.location.pitch || 0,
          bearing: chapter.location.bearing || 0,
          duration: 1500,
          essential: true,
        });

        enterChapter(chapter);
      })
      .onStepExit((response) => {
        const index = Number(response.element.getAttribute('data-chapter-index'));

        // onChapterExit isn't used to turn off layers during the
        // journey: the next chapter entry already resets everything
        // (enterChapter), so applying onChapterExit here would be
        // redundant — and risky, since onStepExit and the neighboring
        // chapter's onStepEnter belong to the same Scrollama event
        // batch, with no guaranteed order between them.
        //
        // We only zero things out here at the real edges — leaving the
        // first chapter upward, or the last one downward — because in
        // those cases there's no next onStepEnter to fix the state.
        const isLeavingStart = index === 0 && response.direction === 'up';
        const isLeavingEnd = index === config.chapters.length - 1 && response.direction === 'down';

        if (isLeavingStart || isLeavingEnd) {
          hideAllLayers();
        }
      });

    window.addEventListener('resize', () => {
      scroller.resize();
    });
  }

  map.on('load', () => {
    moveLabelsToTop(map);

    // Initial state = chapter 1 already visible (no need to wait for the first scroll).
    enterChapter(firstChapter);
    setupScroll();
  });
}

function boot() {
  const roots = document.querySelectorAll('[data-meninas-maes-storymap]');
  roots.forEach(init);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
